import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { calculatorsAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PiggyBank, TrendingUp, Coffee, Calculator, RefreshCw } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const Calculators = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);

  // Savings Calculator
  const [savingsForm, setSavingsForm] = useState({ monthly: '500', months: '12', interest: '3' });
  const [savingsResult, setSavingsResult] = useState(null);
  const [savingsLoading, setSavingsLoading] = useState(false);

  // Investment Calculator
  const [investForm, setInvestForm] = useState({ initial: '1000', monthly: '200', years: '10', profile: 'moderate' });
  const [investResult, setInvestResult] = useState(null);
  const [investLoading, setInvestLoading] = useState(false);

  // Expense Impact Calculator
  const [impactForm, setImpactForm] = useState({ daily: '5', years: '10', rate: '7' });
  const [impactResult, setImpactResult] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const calculateSavings = async () => {
    setSavingsLoading(true);
    try {
      const response = await calculatorsAPI.savings({
        monthly_savings: parseFloat(savingsForm.monthly),
        months: parseInt(savingsForm.months),
        annual_interest: parseFloat(savingsForm.interest)
      });
      setSavingsResult(response.data);
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setSavingsLoading(false);
    }
  };

  const calculateInvestment = async () => {
    setInvestLoading(true);
    try {
      const response = await calculatorsAPI.investment({
        initial_amount: parseFloat(investForm.initial),
        monthly_contribution: parseFloat(investForm.monthly),
        years: parseInt(investForm.years),
        profile: investForm.profile
      });
      setInvestResult(response.data);
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setInvestLoading(false);
    }
  };

  const calculateImpact = async () => {
    setImpactLoading(true);
    try {
      const response = await calculatorsAPI.expenseImpact({
        daily_expense: parseFloat(impactForm.daily),
        years: parseInt(impactForm.years),
        opportunity_cost_rate: parseFloat(impactForm.rate)
      });
      setImpactResult(response.data);
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setImpactLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="calculators-page">
      {/* Header */}
      <div>
        <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
          {t('calculators')}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Herramientas para planificar tu futuro financiero
        </p>
      </div>

      <Tabs defaultValue="savings" className="space-y-6">
        <TabsList className="bg-slate-100 rounded-sm p-1 flex flex-wrap h-auto justify-start gap-1">
          <TabsTrigger value="savings" className="rounded-sm data-[state=active]:bg-white" data-testid="savings-tab">
            <PiggyBank size={16} className="mr-2" />
            {t('savingsCalculator')}
          </TabsTrigger>
          <TabsTrigger value="investment" className="rounded-sm data-[state=active]:bg-white" data-testid="investment-tab">
            <TrendingUp size={16} className="mr-2" />
            {t('investmentCalculator')}
          </TabsTrigger>
          <TabsTrigger value="impact" className="rounded-sm data-[state=active]:bg-white" data-testid="impact-tab">
            <Coffee size={16} className="mr-2" />
            {t('expenseImpact')}
          </TabsTrigger>
        </TabsList>

        {/* Savings Calculator */}
        <TabsContent value="savings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 rounded-sm shadow-none" data-testid="savings-form">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <PiggyBank size={18} className="text-emerald-900" />
                  {t('savingsCalculator')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('monthlySavingsAmount')} ({user?.currency})
                  </Label>
                  <Input
                    type="number"
                    value={savingsForm.monthly}
                    onChange={(e) => setSavingsForm(prev => ({ ...prev, monthly: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="savings-monthly-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('months')}
                  </Label>
                  <Input
                    type="number"
                    value={savingsForm.months}
                    onChange={(e) => setSavingsForm(prev => ({ ...prev, months: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="savings-months-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('interestRate')} (% anual)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={savingsForm.interest}
                    onChange={(e) => setSavingsForm(prev => ({ ...prev, interest: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="savings-interest-input"
                  />
                </div>
                <Button
                  onClick={calculateSavings}
                  className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                  disabled={savingsLoading}
                  data-testid="calculate-savings-btn"
                >
                  {savingsLoading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Calculator size={16} className="mr-2" />}
                  {t('calculate')}
                </Button>
              </CardContent>
            </Card>

            {savingsResult && (
              <Card className="border-slate-200 rounded-sm shadow-none" data-testid="savings-result">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-slate-900">
                    {t('result')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-sm">
                    <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700 mb-1">
                      {t('projectedValue')}
                    </p>
                    <p className="font-mono text-3xl font-bold text-emerald-900">
                      {formatCurrency(savingsResult.final_amount, user?.currency)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">{t('totalContributed')}</p>
                      <p className="font-mono text-lg font-medium text-slate-900">
                        {formatCurrency(savingsResult.total_contributed, user?.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">{t('interestEarned')}</p>
                      <p className="font-mono text-lg font-medium text-emerald-600">
                        {formatCurrency(savingsResult.interest_earned, user?.currency)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Investment Calculator */}
        <TabsContent value="investment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 rounded-sm shadow-none" data-testid="investment-form">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <TrendingUp size={18} className="text-emerald-900" />
                  {t('investmentCalculator')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    Capital Inicial ({user?.currency})
                  </Label>
                  <Input
                    type="number"
                    value={investForm.initial}
                    onChange={(e) => setInvestForm(prev => ({ ...prev, initial: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="invest-initial-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    Aportación Mensual ({user?.currency})
                  </Label>
                  <Input
                    type="number"
                    value={investForm.monthly}
                    onChange={(e) => setInvestForm(prev => ({ ...prev, monthly: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="invest-monthly-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('years')}
                  </Label>
                  <Input
                    type="number"
                    value={investForm.years}
                    onChange={(e) => setInvestForm(prev => ({ ...prev, years: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="invest-years-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    Perfil de Riesgo
                  </Label>
                  <Select value={investForm.profile} onValueChange={(v) => setInvestForm(prev => ({ ...prev, profile: v }))}>
                    <SelectTrigger className="rounded-sm" data-testid="invest-profile-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservador (4% anual)</SelectItem>
                      <SelectItem value="moderate">Moderado (7% anual)</SelectItem>
                      <SelectItem value="aggressive">Agresivo (10% anual)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={calculateInvestment}
                  className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                  disabled={investLoading}
                  data-testid="calculate-investment-btn"
                >
                  {investLoading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Calculator size={16} className="mr-2" />}
                  {t('calculate')}
                </Button>
              </CardContent>
            </Card>

            {investResult && (
              <Card className="border-slate-200 rounded-sm shadow-none" data-testid="investment-result">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-slate-900">
                    {t('result')} - Perfil {investResult.profile}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-sm">
                    <p className="text-xs uppercase tracking-widest font-semibold text-emerald-700 mb-1">
                      Valor Final Proyectado
                    </p>
                    <p className="font-mono text-3xl font-bold text-emerald-900">
                      {formatCurrency(investResult.final_value, user?.currency)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-400">{t('totalContributed')}</p>
                      <p className="font-mono text-lg font-medium text-slate-900">
                        {formatCurrency(investResult.total_contributions, user?.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Ganancias</p>
                      <p className="font-mono text-lg font-medium text-emerald-600">
                        {formatCurrency(investResult.total_earnings, user?.currency)}
                      </p>
                    </div>
                  </div>
                  {investResult.yearly_breakdown && (
                    <div className="h-48 mt-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={investResult.yearly_breakdown}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                          <Tooltip formatter={(v) => formatCurrency(v, user?.currency)} />
                          <Line type="monotone" dataKey="value" stroke="#064E3B" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Expense Impact Calculator */}
        <TabsContent value="impact">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 rounded-sm shadow-none" data-testid="impact-form">
              <CardHeader>
                <CardTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Coffee size={18} className="text-amber-600" />
                  Impacto de Gastos Pequeños
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Descubre cuánto cuesta realmente ese café diario o suscripción mensual a largo plazo.
                </p>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    Gasto Diario ({user?.currency})
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={impactForm.daily}
                    onChange={(e) => setImpactForm(prev => ({ ...prev, daily: e.target.value }))}
                    className="font-mono rounded-sm"
                    placeholder="Ej: 3.50 (un café)"
                    data-testid="impact-daily-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('years')}
                  </Label>
                  <Input
                    type="number"
                    value={impactForm.years}
                    onChange={(e) => setImpactForm(prev => ({ ...prev, years: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="impact-years-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    Rentabilidad Alternativa (% anual)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={impactForm.rate}
                    onChange={(e) => setImpactForm(prev => ({ ...prev, rate: e.target.value }))}
                    className="font-mono rounded-sm"
                    data-testid="impact-rate-input"
                  />
                </div>
                <Button
                  onClick={calculateImpact}
                  className="w-full bg-amber-600 hover:bg-amber-700 rounded-sm"
                  disabled={impactLoading}
                  data-testid="calculate-impact-btn"
                >
                  {impactLoading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Calculator size={16} className="mr-2" />}
                  {t('calculate')}
                </Button>
              </CardContent>
            </Card>

            {impactResult && (
              <Card className="border-slate-200 rounded-sm shadow-none" data-testid="impact-result">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold text-slate-900">
                    El verdadero coste de {formatCurrency(impactResult.daily_expense, user?.currency)}/día
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-400 mb-1">Gasto Anual</p>
                      <p className="font-mono text-xl font-medium text-slate-900">
                        {formatCurrency(impactResult.annual_expense, user?.currency)}
                      </p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-sm">
                      <p className="text-xs text-red-600 mb-1">Total en {impactResult.years} años</p>
                      <p className="font-mono text-xl font-medium text-red-600">
                        {formatCurrency(impactResult.total_over_years, user?.currency)}
                      </p>
                    </div>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-sm">
                    <p className="text-xs text-emerald-700 mb-1">
                      Si lo hubieras invertido al {impactResult.if_invested_at_rate}% anual
                    </p>
                    <p className="font-mono text-2xl font-bold text-emerald-900">
                      {formatCurrency(impactResult.invested_value, user?.currency)}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-sm border border-amber-200">
                    <p className="text-xs text-amber-700 mb-1">Coste de Oportunidad</p>
                    <p className="font-mono text-xl font-bold text-amber-700">
                      {formatCurrency(impactResult.opportunity_cost, user?.currency)}
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Esto es lo que "pierdes" por no invertir ese dinero
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Calculators;
